import { useEffect, useRef, useState } from "react";
import GlassContainer from "@/shared/components/glasscontainer";
import Button from "@/shared/components/button";
import { toast } from "@/shared/components/toast";
import { useBranding } from "../hooks/useBranding";
import { useBrandingTheme } from "../hooks/BrandingProvider";
import BrandingService from "../services/BrandingService";
import type {
  BrandingColors,
  DomainStatus,
  TenantBranding,
} from "../models/Branding";

const service = new BrandingService();

const COLOR_FIELDS: { key: keyof BrandingColors; label: string }[] = [
  { key: "accent", label: "Acento" },
  { key: "foreground", label: "Texto" },

];

const DOMAIN_BADGE: Record<DomainStatus, { label: string; cls: string }> = {
  None: { label: "Sin dominio", cls: "bg-foreground/10 text-foreground/60" },
  Pending: { label: "Pendiente", cls: "bg-amber-500/15 text-amber-600" },
  Verified: { label: "Verificado", cls: "bg-emerald-500/15 text-emerald-600" },
  Failed: { label: "Falló", cls: "bg-red-500/15 text-red-600" },
};

export default function BrandingPage() {
  const {
    loading,
    get,
    updateAppearance,
    uploadLogo,
    uploadFavicon,
    setDomain,
    verifyDomain,
  } = useBranding();
  const { applyColors } = useBrandingTheme();

  const [branding, setBranding] = useState<TenantBranding>();
  const [appName, setAppName] = useState("");
  const [colors, setColors] = useState<BrandingColors>({});
  const [domainInput, setDomainInput] = useState("");
  const logoRef = useRef<HTMLInputElement>(null);
  const faviconRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let ignore = false;
    get().then((b) => {
      if (ignore || !b) return;
      setBranding(b);
      setAppName(b.appName ?? "");
      setColors(b.colors ?? {});
      setDomainInput(b.customDomain ?? "");
    });
    return () => {
      ignore = true;
    };
  }, [get]);

  const onColorChange = (key: keyof BrandingColors, value: string) => {
    const next = { ...colors, [key]: value };
    setColors(next);
    applyColors(next); // live preview
  };

  const onSaveAppearance = async () => {
    const b = await updateAppearance(appName.trim() || null, colors);
    if (b) setBranding(b);
  };

  const onLogoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b = await uploadLogo(file);
    if (b) setBranding(b);
    if (logoRef.current) logoRef.current.value = "";
  };

  const onFaviconSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b = await uploadFavicon(file);
    if (b) setBranding(b);
    if (faviconRef.current) faviconRef.current.value = "";
  };

  const onSaveDomain = async () => {
    const b = await setDomain(domainInput.trim());
    if (b) setBranding(b);
  };

  const onVerify = async () => {
    const b = await verifyDomain();
    if (b) {
      setBranding(b);
      if (b.domainStatus === "Verified") toast.success("Dominio verificado");
      else toast.error("No se encontró el registro DNS todavía");
    }
  };

  const logoUrl = service.resolveAssetUrl(branding?.logoUrl);
  const faviconUrl = service.resolveAssetUrl(branding?.faviconUrl);
  const status = branding?.domainStatus ?? "None";

  return (
    <div className="min-h-full bg-[#f0f0f3] text-foreground p-8">
      <header className="mb-8">
        <h2 className="text-[40px] font-bold tracking-tight">Marca</h2>
        <p className="text-foreground/50 text-lg font-medium mt-1">
          Personaliza el nombre, el logo, el favicon, los colores y el dominio.
          Tus usuarios heredan esta configuración.
        </p>
      </header>

      <div className="grid gap-6 max-w-4xl">
        {/* App name */}
        <GlassContainer className="p-6">
          <h3 className="text-xl font-semibold mb-4">Nombre de la aplicación</h3>
          <input
            type="text"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            placeholder="Mi Plataforma"
            maxLength={100}
            className="w-full max-w-md px-4 py-2.5 rounded-xl bg-white/60 border border-black/10 text-sm focus:outline-none focus:border-accent"
          />
          <p className="text-foreground/40 text-xs mt-2">
            Se muestra en la pestaña del navegador. Se guarda junto con los
            colores.
          </p>
        </GlassContainer>

        {/* Logo & favicon */}
        <GlassContainer className="p-6">
          <h3 className="text-xl font-semibold mb-4">Logo y favicon</h3>
          <div className="flex flex-wrap items-start gap-10">
            <div className="flex items-center gap-6">
              <div className="size-24 rounded-2xl bg-white shadow-inner flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-foreground/30 text-sm">Sin logo</span>
                )}
              </div>
              <div>
                <input
                  ref={logoRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,.webp"
                  className="hidden"
                  onChange={onLogoSelected}
                />
                <Button
                  variant="secondary"
                  onClick={() => logoRef.current?.click()}
                  disabled={loading}
                >
                  Subir logo
                </Button>
                <p className="text-foreground/40 text-xs mt-2">
                  PNG, JPG, SVG o WebP. Máx 2 MB.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="size-16 rounded-xl bg-white shadow-inner flex items-center justify-center overflow-hidden">
                {faviconUrl ? (
                  <img
                    src={faviconUrl}
                    alt="Favicon"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-foreground/30 text-xs">Sin favicon</span>
                )}
              </div>
              <div>
                <input
                  ref={faviconRef}
                  type="file"
                  accept=".png,.svg,.ico"
                  className="hidden"
                  onChange={onFaviconSelected}
                />
                <Button
                  variant="secondary"
                  onClick={() => faviconRef.current?.click()}
                  disabled={loading}
                >
                  Subir favicon
                </Button>
                <p className="text-foreground/40 text-xs mt-2">
                  PNG, SVG o ICO. Máx 2 MB.
                </p>
              </div>
            </div>
          </div>
        </GlassContainer>

        {/* Colors */}
        <GlassContainer className="p-6">
          <h3 className="text-xl font-semibold mb-4">Colores</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {COLOR_FIELDS.map(({ key, label }) => (
              <label
                key={key}
                className="flex items-center gap-3 bg-white/50 rounded-xl px-3 py-2"
              >
                <input
                  type="color"
                  value={colors[key] ?? "#000000"}
                  onChange={(e) => onColorChange(key, e.target.value)}
                  className="size-8 rounded-lg cursor-pointer border border-black/10 bg-transparent"
                />
                <span className="text-sm font-medium">{label}</span>
              </label>
            ))}
          </div>
          <div className="mt-5">
            <Button onClick={onSaveAppearance} disabled={loading}>
              Guardar nombre y colores
            </Button>
          </div>
        </GlassContainer>

        {/* Custom domain */}
        <GlassContainer className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Dominio personalizado</h3>
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full ${DOMAIN_BADGE[status].cls}`}
            >
              {DOMAIN_BADGE[status].label}
            </span>
          </div>

          <div className="flex gap-3 items-center">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="app.tudominio.com"
              className="flex-1 px-4 py-2.5 rounded-xl bg-white/60 border border-black/10 text-sm focus:outline-none focus:border-accent"
            />
            <Button
              variant="secondary"
              onClick={onSaveDomain}
              disabled={loading || !domainInput.trim()}
            >
              Guardar
            </Button>
          </div>

          {branding?.verificationRecordName && status !== "Verified" && (
            <div className="mt-5 bg-white/60 rounded-xl p-4 text-sm">
              <p className="font-semibold mb-2">
                Crea este registro DNS para verificar la propiedad:
              </p>
              <dl className="space-y-1 font-mono text-xs">
                <div className="flex gap-2">
                  <dt className="text-foreground/50 w-16">Tipo</dt>
                  <dd>TXT</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground/50 w-16">Nombre</dt>
                  <dd className="break-all">
                    {branding.verificationRecordName}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground/50 w-16">Valor</dt>
                  <dd className="break-all">
                    {branding.verificationRecordValue}
                  </dd>
                </div>
              </dl>
              <p className="text-foreground/50 mt-3">
                Apunta también tu dominio (CNAME) a la plataforma. La propagación
                DNS puede tardar unos minutos.
              </p>
              <div className="mt-3">
                <Button onClick={onVerify} disabled={loading}>
                  Verificar dominio
                </Button>
              </div>
            </div>
          )}
        </GlassContainer>
      </div>
    </div>
  );
}
